<template>
  <li class="certificate-types-management">
    <div class="card rounded">
      <div class="p-3 d-flex justify-content-between align-items-center">
        <h4 class="mb-0">{{ $t("general.certificate_type") }}</h4>
        <button class="btn btn-primary btn-sm" type="button" @click="openCreateModal">
          <i class="uil-plus"></i> {{ $t("general.add") }}
        </button>
      </div>
      <hr class="m-0"/>

      <div class="p-3">
        <div class="form-group mb-3">
          <input v-model="searchQuery"
                 :placeholder="$t('general.search')"
                 class="form-control"
                 type="text"
                 @input="handleSearch"/>
        </div>

        <!-- Table -->
        <div v-if="certificateTypes && certificateTypes.length > 0 || loading"
             style="position: relative; min-height: 200px;">
          <!-- Loading Overlay -->
          <div v-if="loading" style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; z-index: 10;">
            <DefaultLoading :minHeight="'200px'"/>
          </div>

          <div v-if="certificateTypes && certificateTypes.length > 0" :style="{ opacity: loading ? 0.3 : 1 }">
            <div class="table-responsive">
              <table class="table table-hover certificate-types-table">
                <thead>
                <tr>
                  <th>{{ $t("general.name") }}</th>
                  <th>{{ $t("general.created_at") }}</th>
                  <th class="text-right actions-column"></th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="type in certificateTypes" :key="type.id">
                  <td class="name-cell">{{ type.name }}</td>
                  <td class="date-cell">{{ formatDate(type.created_at) }}</td>
                  <td class="text-right actions-cell">
                    <button :title="$t('general.edit')" class="btn-icon btn-icon-warning" type="button"
                            @click="openEditModal(type)">
                      <i class="uil-edit"></i>
                    </button>
                    <button :title="$t('general.delete')" class="btn-icon btn-icon-danger" type="button"
                            @click="confirmDelete(type)">
                      <i class="uil-trash"></i>
                    </button>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div v-if="hasMultiplePages" class="d-flex justify-content-between align-items-center ">
              <Pagination :pagination="pagination" @changePage="goToPage"/>
            </div>
          </div>
        </div>

        <!-- No Data -->
        <div v-else class="text-center py-4">
          <p class="text-muted">{{ $t("general.no_data") }}</p>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div v-if="showModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content" style="position: relative;" @click.stop>
        <!-- Modal Loading -->
        <div v-if="savingType" class="modal-loading-overlay">
          <DefaultLoading :minHeight="'100px'"/>
        </div>

        <div :style="{ opacity: savingType ? 0.3 : 1 }">
          <div class="modal-header">
            <h5>{{ isEditMode ? $t("general.edit") : $t("general.add") }}</h5>
            <button :disabled="savingType" class="close-btn" type="button" @click="closeModal">×</button>
          </div>
          <div class="modal-body">
            <div class="form-group">
              <label class="col-form-label">{{ $t("general.name") }}<span class="required">*</span></label>
              <input v-model="formData.name"
                     :disabled="savingType"
                     class="form-control"
                     type="text"/>
            </div>
          </div>
          <div class="modal-footer">
            <button :disabled="savingType" class="btn btn-secondary" type="button" @click="closeModal">
              {{ $t("general.cancel") }}
            </button>
            <button :disabled="!formData.name || savingType" class="btn btn-primary" type="button" @click="saveType">
              {{ savingType ? $t("general.saving") : $t("general.save") }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="modal-overlay" @click="!deletingType && (showDeleteModal = false)">
      <div class="modal-content" style="position: relative;" @click.stop>
        <!-- Modal Loading -->
        <div v-if="deletingType" class="modal-loading-overlay">
          <DefaultLoading :minHeight="'100px'"/>
        </div>

        <div :style="{ opacity: deletingType ? 0.3 : 1 }">
          <div class="modal-header">
            <h5>{{ $t("general.delete") }}</h5>
            <button :disabled="deletingType" class="close-btn" type="button" @click="showDeleteModal = false">×</button>
          </div>
          <div class="modal-body">
            <p>{{ $t("general.sure_to_delete") }}</p>
            <p class="font-weight-bold">{{ typeToDelete?.name }}</p>
          </div>
          <div class="modal-footer">
            <button :disabled="deletingType" class="btn btn-secondary" type="button" @click="showDeleteModal = false">
              {{ $t("general.cancel") }}
            </button>
            <button :disabled="deletingType" class="btn btn-danger" type="button" @click="deleteType">
              {{ deletingType ? $t("general.deleting") : $t("general.delete") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </li>
</template>

<script>
import store from "@/core/services";
import moment from "moment";
import Pagination from "@/assets/components/Pagination";
import DefaultLoading from "@/view/components/loading/DefaultLoading";
import module, {
  BASE_URL,
  CREATE_ITEM,
  DELETE_ITEM,
  GET_ITEMS,
  UPDATE_ITEM,
  ITEMS,
  MODULE_NAME,
  LOADING,
  ERROR,
  PAGINATION,
} from "@/core/services/store/certificate_type.module";

export default {
  name: "CertificateTypesManagement",
  components: {
    Pagination,
    DefaultLoading
  },
  data() {
    return {
      searchQuery: "",
      showModal: false,
      showDeleteModal: false,
      isEditMode: false,
      formData: {
        id: null,
        name: "",
      },
      typeToDelete: null,
      searchTimeout: null,
      currentPage: 1,
      perPage: 10,
      savingType: false,
      deletingType: false,
    };
  },
  beforeCreate() {
    if (!(store && store.state && store.state[MODULE_NAME])) {
      store.registerModule(MODULE_NAME, module);
    }
  },
  computed: {
    certificateTypes() {
      return store.getters[MODULE_NAME + "/" + ITEMS];
    },
    loading() {
      return store.getters[MODULE_NAME + "/" + LOADING];
    },
    error() {
      return store.getters[MODULE_NAME + "/" + ERROR];
    },
    pagination() {
      return store.getters[MODULE_NAME + "/" + PAGINATION];
    },
    totalPages() {
      return this.pagination?.last_page || 1;
    },
    hasMultiplePages() {
      return this.totalPages > 1;
    },
  },
  methods: {
    loadCertificateTypes(search = "", page = null) {
      const filters = {
        page: page || this.currentPage,
        per_page: this.perPage,
      };
      if (search) {
        filters.like = search;
      }
      this.$store.dispatch(MODULE_NAME + "/" + GET_ITEMS, {
        url: BASE_URL,
        filters,
      });
    },
    handleSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.loadCertificateTypes(this.searchQuery, 1);
      }, 500);
    },
    openCreateModal() {
      this.isEditMode = false;
      this.formData = { id: null, name: "" };
      this.showModal = true;
    },
    openEditModal(type) {
      this.isEditMode = true;
      this.formData = { id: type.id, name: type.name };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.formData = { id: null, name: "" };
    },
    saveType() {
      if (!this.formData.name || this.formData.name.trim() === "") {
        return;
      }

      this.savingType = true;

      const payload = {
        url: this.isEditMode ? `${BASE_URL}/${this.formData.id}` : BASE_URL,
        contents: { name: this.formData.name.trim() },
      };

      const action = this.isEditMode ? UPDATE_ITEM : CREATE_ITEM;

      this.$store.dispatch(MODULE_NAME + "/" + action, payload).then(() => {
        this.savingType = false;
        if (!this.error) {
          this.closeModal();
          this.loadCertificateTypes(this.searchQuery, this.currentPage);
          this.$emit('typesUpdated');
        }
      }).catch(() => {
        this.savingType = false;
      });
    },
    confirmDelete(type) {
      this.typeToDelete = type;
      this.showDeleteModal = true;
    },
    deleteType() {
      if (!this.typeToDelete) return;

      this.deletingType = true;

      this.$store
          .dispatch(MODULE_NAME + "/" + DELETE_ITEM, {
            url: `${BASE_URL}/${this.typeToDelete.id}`,
          })
          .then(() => {
            this.deletingType = false;
            if (!this.error) {
              this.showDeleteModal = false;
              this.typeToDelete = null;

              // If we're on the last page and there's only one item, go to previous page
              if (this.certificateTypes.length === 1 && this.currentPage > 1) {
                this.currentPage--;
              }

              this.loadCertificateTypes(this.searchQuery, this.currentPage);
              this.$emit('typesUpdated');
            }
          })
          .catch(() => {
            this.deletingType = false;
          });
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.loadCertificateTypes(this.searchQuery, page);
      }
    },
    formatDate(date) {
      if (!date) return '-';
      return moment(date).format('DD.MM.YYYY HH:mm');
    },
  },
  mounted() {
    this.loadCertificateTypes();
  },
};
</script>

<style scoped>
/* Table Styles */
.certificate-types-table {
  font-size: 0.9rem;
}

.certificate-types-table thead th {
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #495057;
  padding: 0.6rem 0.75rem;
}

.certificate-types-table tbody tr {
  transition: background-color 0.2s ease;
}

.certificate-types-table tbody tr:hover {
  background-color: #f8f9fa;
}

.certificate-types-table tbody td {
  padding: 0.5rem 0.75rem;
  vertical-align: middle;
  border-bottom: 1px solid #e9ecef;
}

.name-cell {
  font-weight: 500;
  color: #212529;
}

.date-cell {
  color: #6c757d;
  font-size: 0.875rem;
  white-space: nowrap;
}

.actions-column {
  width: 100px;
}

.actions-cell {
  white-space: nowrap;
}

/* Icon Buttons */
.btn-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px !important;
  height: 32px !important;
  padding: 0 !important;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: 3px;
}

.btn-icon i {
  font-size: 0.9rem;
  line-height: 1;
}

.btn-icon-warning {
  background-color: #fff3cd;
  color: #856404;
}

.btn-icon-warning:hover {
  background-color: #ffc107;
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn-icon-danger {
  background-color: #f8d7da;
  color: #721c24;
}

.btn-icon-danger:hover {
  background-color: #dc3545;
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  min-width: 350px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h5 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #212529;
}

.close-btn {
  background: none;
  border: none;
  font-size: 2rem;
  line-height: 1;
  cursor: pointer;
  color: #6b7280;
  transition: color 0.2s ease;
}

.close-btn:hover {
  color: #111827;
}

.modal-body {
  padding: 1.5rem;
}

.modal-body p {
  font-size: 1rem;
  line-height: 1.5;
  color: #495057;
  margin-bottom: 0.75rem;
}

.modal-body p:last-child {
  margin-bottom: 0;
}

.modal-body .font-weight-bold {
  font-size: 1.125rem;
  color: #212529;
  margin-top: 0.5rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
}

.modal-footer .btn {
  font-size: 1rem;
  padding: 0.5rem 1.25rem;
  font-weight: 500;
}

.required {
  color: red;
  margin-left: 2px;
}

.form-group label {
  font-size: 1rem;
  font-weight: 500;
  color: #212529;
}

.form-group .form-control {
  font-size: 1rem;
}

/* Modal Loading Overlay */
.modal-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.9);
  z-index: 10;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
